import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Gauge } from 'lucide-react';
import type { PerformanceCycleCriterion, PerformanceCycleStatus, PerformanceReviewList, PerformanceReviewStatus } from '@mnc/contracts';
import { api } from '../../lib/api';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';
import { stripBidiMarks } from '../../lib/text';

type Tone = 'neutral' | 'brand' | 'warn' | 'danger' | 'info';
export const CYCLE_TONE: Record<PerformanceCycleStatus, Tone> = { draft: 'info', open: 'brand', closed: 'neutral' };
export const REVIEW_TONE: Record<PerformanceReviewStatus, Tone> = { draft: 'warn', submitted: 'info', acknowledged: 'brand' };
export const SCORES = [0, 1, 2, 3, 4, 5];

export const fmtDate = (iso: string | null, language: string) => (iso ? stripBidiMarks(new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(language, { dateStyle: 'medium', timeZone: 'UTC' })) : '—');
export const fmtPeriod = (start: string, end: string, language: string) => `${fmtDate(start, language)} → ${fmtDate(end, language)}`;

/** What a score means for a criterion, in the reader's language (empty when the criterion describes none). */
export function levelText(c: Pick<PerformanceCycleCriterion, 'levels'>, score: number | null, language: string): string {
  if (score === null) return '';
  const l = c.levels.find((x) => x.score === score);
  return l ? pick(language, l.description_en, l.description_ar) : '';
}

/** "348 / 500 · 69.6%": MRLCO's points, and the share of the maximum. */
export function Points({ points, percent, className }: { points: number; percent: number; className?: string }) {
  const { i18n } = useTranslation();
  const n = (v: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(v);
  return <span className={cn('tabular-nums', className)} dir="ltr">{n(points)} / 500 · {n(percent)}%</span>;
}

export function ReviewBadge({ status }: { status: PerformanceReviewStatus | null }) {
  const { t } = useTranslation();
  return <Badge tone={status ? REVIEW_TONE[status] : 'neutral'}>{t(`performance.reviewStatuses.${status ?? 'none'}`)}</Badge>;
}

/**
 * Six buttons, 0 to 5; each says what it means (the criterion's own description), and the chosen one is spelled out
 * below. `null` = not scored yet, which is not the same as 0.
 */
export function ScoreButtons({ criterion, value, onChange, disabled, self }: { criterion: PerformanceCycleCriterion; value: number | null; onChange?: (v: number) => void; disabled?: boolean; self?: number | null }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={pick(i18n.language, criterion.name_en, criterion.name_ar)}>
        {SCORES.map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={value === s}
            disabled={disabled || !onChange}
            title={levelText(criterion, s, i18n.language)}
            onClick={() => onChange?.(s)}
            className={cn('relative h-8 w-8 rounded-lg border text-sm tabular-nums transition', value === s ? 'border-brand-600 bg-brand-600 text-white' : 'border-neutral-300 bg-white hover:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900', (disabled || !onChange) && value !== s && 'opacity-60')}
          >
            {s}
            {self === s && <span className="absolute -top-1 -end-1 h-2.5 w-2.5 rounded-full border border-white bg-amber-500 dark:border-neutral-900" title={t('performance.selfScore')} />}
          </button>
        ))}
      </div>
      <p className="min-h-4 text-xs text-neutral-500" dir="auto">{value === null ? t('performance.notScored') : levelText(criterion, value, i18n.language)}</p>
    </div>
  );
}

/**
 * The spider chart MRLCO showed on "My performance", drawn here without a chart library: one axis per criterion,
 * rings at each score, the review's polygon, and (dashed) the previous one to compare with.
 */
export function RadarChart({ criteria, scores, previous, size = 320 }: { criteria: PerformanceCycleCriterion[]; scores: Array<number | null>; previous?: Array<number | null> | null; size?: number }) {
  const { i18n } = useTranslation();
  const n = criteria.length;
  if (n < 3) return null;
  const c = 200;
  const r = 130;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const point = (i: number, v: number) => [c + (r * v * Math.cos(angle(i))) / 5, c + (r * v * Math.sin(angle(i))) / 5] as const;
  const poly = (values: Array<number | null>) => values.map((v, i) => point(i, v ?? 0).join(',')).join(' ');
  return (
    <svg viewBox="0 0 400 400" width="100%" style={{ maxWidth: size }} className="mx-auto block" role="img" aria-label="radar">
      {[1, 2, 3, 4, 5].map((ring) => <polygon key={ring} points={criteria.map((_x, i) => point(i, ring).join(',')).join(' ')} className="fill-none stroke-neutral-200 dark:stroke-neutral-700" strokeWidth={1} />)}
      {criteria.map((_x, i) => <line key={i} x1={c} y1={c} x2={point(i, 5)[0]} y2={point(i, 5)[1]} className="stroke-neutral-200 dark:stroke-neutral-700" strokeWidth={1} />)}
      {previous && <polygon points={poly(previous)} className="fill-neutral-400/10 stroke-neutral-400" strokeWidth={1.5} strokeDasharray="5 4" />}
      <polygon points={poly(scores)} className="fill-brand-500/25 stroke-brand-600" strokeWidth={2} />
      {scores.map((v, i) => v !== null && <circle key={i} cx={point(i, v)[0]} cy={point(i, v)[1]} r={3.5} className="fill-brand-600"><title>{`${pick(i18n.language, criteria[i]!.name_en, criteria[i]!.name_ar)}: ${v} — ${levelText(criteria[i]!, v, i18n.language)}`}</title></circle>)}
      {criteria.map((x, i) => {
        const [lx, ly] = point(i, 6.1);
        const anchor = Math.abs(lx - c) < 8 ? 'middle' : lx > c ? 'start' : 'end';
        return <text key={x.id} x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" className="fill-neutral-600 text-[11px] dark:fill-neutral-300">{pick(i18n.language, x.name_en, x.name_ar)}</text>;
      })}
    </svg>
  );
}

/** A small line of totals across cycles (oldest to newest), for the employee page. */
export function Trend({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const w = 160;
  const h = 40;
  const x = (i: number) => (i * w) / (points.length - 1);
  const y = (p: number) => h - (p / 500) * h;
  return (
    <svg viewBox={`-4 -4 ${w + 8} ${h + 8}`} width={w} height={h} aria-hidden>
      <polyline points={points.map((p, i) => `${x(i)},${y(p)}`).join(' ')} className="fill-none stroke-brand-600" strokeWidth={2} />
      {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p)} r={2.5} className="fill-brand-600" />)}
    </svg>
  );
}

/** The employee page's performance card: shared reviews, the trend of totals, and the latest objectives. */
export function PerformancePanel({ employeeId }: { employeeId: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const q = useQuery({ queryKey: ['performance', 'employee', employeeId], queryFn: () => api.get<PerformanceReviewList>(`/api/performance/employees/${employeeId}/reviews`), retry: false });
  if (!q.data) return null;
  const items = q.data.items;
  const latest = items[0];
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-semibold"><Gauge size={16} className="text-neutral-500" />{t('performance.panelTitle')}</h2>
        <Trend points={[...items].reverse().map((r) => r.total_points)} />
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-neutral-500">{t('performance.noReviewsFor')}</p>
      ) : (
        <>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {items.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="min-w-0 flex-1 font-medium" dir="auto">{pick(lang, r.cycle.name_en, r.cycle.name_ar)}</span>
                <Points points={r.total_points} percent={r.percent} />
                <ReviewBadge status={r.status} />
              </li>
            ))}
          </ul>
          {latest?.objectives && <div className="mt-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm dark:bg-neutral-800/60"><div className="text-xs font-semibold text-neutral-500">{t('performance.objectives')}</div><p className="whitespace-pre-wrap" dir="auto">{latest.objectives}</p></div>}
        </>
      )}
    </div>
  );
}
